import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Button, Stack, Pagination, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Tabs, Tab, Grid, Card, CardContent,
  Switch, Divider, FormControl, Select, MenuItem, IconButton, alpha,
  CircularProgress, Avatar,
} from '@mui/material';
import {
  Cancel, Bookmark, Settings, Refresh, CheckCircle, Warning, Timer,
  Policy, Search,
} from '@mui/icons-material';
import { adminAPI, settingsAPI } from '../../api/client';
import { integerOnly, decimalOnly } from '../../utils/inputHelpers';

const statusColors = { pending: 'warning', confirmed: 'success', cancelled: 'error', completed: 'info', expired: 'default' };

function AdminBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [bs, setBs] = useState({
    auto_confirm: 'false', require_payment_upfront: 'true',
    max_duration_months: '12', min_duration_months: '1',
    cancel_fee_percent: '10', cancel_window_hours: '48',
    allow_instant: 'true', require_kyc: 'true',
    max_pending: '3', expiry_hours: '24',
    platform_fee_percentage: '5', platform_fee_mode: 'per_booking', platform_fee_enabled: 'true',
  });

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 15 };
      if (statusFilter) params.status = statusFilter;
      const res = await adminAPI.listBookings(params);
      setBookings(res.data.bookings || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBookings(); }, [page, statusFilter]);
  useEffect(() => {
    settingsAPI.list({ category: 'booking' }).then(res => {
      if (Array.isArray(res.data)) {
        const m = {}; res.data.forEach(s => { m[s.key] = s.value; });
        setBs(p => ({ ...p, ...m }));
      }
    }).catch(() => {});
    settingsAPI.list({ category: 'platform_fee' }).then(res => {
      if (Array.isArray(res.data)) {
        const m = {}; res.data.forEach(s => { m[s.key] = s.value; });
        setBs(p => ({ ...p, ...m }));
      }
    }).catch(() => {});
  }, []);

  const handleCancel = async () => {
    if (!cancelDialog || !cancelReason) return;
    try {
      await adminAPI.cancelBooking(cancelDialog, cancelReason);
      setCancelDialog(null); setCancelReason('');
      setSuccess('Booking cancelled'); fetchBookings();
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
  };

  const handleApprove = async (bookingId) => {
    try {
      await adminAPI.confirmBooking(bookingId);
      setSuccess('Booking approved and confirmed successfully');
      fetchBookings();
    } catch (err) { setError(err.response?.data?.detail || 'Failed to approve booking'); }
  };

  const saveSetting = async (key, value) => {
    try {
      await settingsAPI.update(key, { value: String(value) });
      setBs(p => ({ ...p, [key]: String(value) }));
      setSuccess('Setting updated');
    } catch (_) { setError('Failed to update'); }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Booking Management
          </Typography>
          <Typography variant="body2" color="text.secondary">View and manage all bookings and applications</Typography>
        </Box>
        <IconButton onClick={fetchBookings} sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="All Bookings" icon={<Bookmark sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Booking Settings" icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Platform Fees" icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Cancellation Policy" icon={<Policy sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total', count: total, color: '#6366f1', icon: <Bookmark /> },
              { label: 'Confirmed', count: bookings.filter(b => b.status === 'confirmed').length, color: '#10b981', icon: <CheckCircle /> },
              { label: 'Pending', count: bookings.filter(b => b.status === 'pending').length, color: '#f59e0b', icon: <Timer /> },
              { label: 'Cancelled', count: bookings.filter(b => b.status === 'cancelled').length, color: '#ef4444', icon: <Cancel /> },
            ].map((item, i) => (
              <Grid item xs={6} sm={3} key={i}>
                <Card sx={{ borderLeft: `4px solid ${item.color}` }}>
                  <CardContent sx={{ py: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, width: 36, height: 36 }}>{item.icon}</Avatar>
                      <Box><Typography variant="body2" color="text.secondary">{item.label}</Typography><Typography variant="h5" fontWeight={800}>{item.count}</Typography></Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select value={statusFilter} displayEmpty onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                <MenuItem value="">All Statuses</MenuItem>
                {['pending','confirmed','cancelled','completed','expired'].map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Paper>

          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Tenant</TableCell><TableCell>Property</TableCell><TableCell>Room</TableCell>
                <TableCell>Check-in</TableCell><TableCell>Check-out</TableCell><TableCell>Amount</TableCell>
                <TableCell>Platform Fee</TableCell><TableCell>Pairing</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {loading ? <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                : bookings.length === 0 ? <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}>No bookings found</TableCell></TableRow>
                : bookings.map((b) => (
                  <TableRow key={b.id} hover>
                    <TableCell>
                      <Typography fontWeight={600} variant="body2">
                        {b.tenant ? `${b.tenant.first_name} ${b.tenant.last_name}` : b.tenant_id?.toString().slice(0,8)}
                      </Typography>
                      {b.tenant?.email && <Typography variant="caption" color="text.secondary">{b.tenant.email}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} variant="body2" noWrap sx={{ maxWidth: 180 }}>
                        {b.property?.title || b.property_id?.toString().slice(0,8)}
                      </Typography>
                      {b.property?.property_type && <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{b.property.property_type.replace('_',' ')}</Typography>}
                    </TableCell>
                    <TableCell>
                      {b.room_number ? (
                        <Box>
                          <Typography variant="body2" fontWeight={500}>#{b.room_number}</Typography>
                          {b.room_detail?.room_type && <Chip label={b.room_detail.room_type} size="small" variant="outlined" sx={{ textTransform: 'capitalize', mt: 0.5 }} />}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>{new Date(b.check_in_date).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(b.check_out_date).toLocaleDateString()}</TableCell>
                    <TableCell><Typography fontWeight={600} variant="body2">GH₵ {(b.total_amount || 0).toLocaleString()}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">GH₵ {(b.platform_fee || 0).toLocaleString()}</Typography></TableCell>
                    <TableCell>
                      <Chip label={b.is_paired ? 'Paired' : 'Solo'} size="small"
                        color={b.is_paired ? 'info' : 'default'} variant={b.is_paired ? 'filled' : 'outlined'} />
                    </TableCell>
                    <TableCell><Chip label={b.status?.replace('_',' ')} size="small" color={statusColors[b.status]||'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {b.status === 'pending' && (
                          <Button size="small" color="success" variant="contained" startIcon={<CheckCircle />} onClick={() => handleApprove(b.id)} sx={{ px: 1.5 }}>Approve</Button>
                        )}
                        {(b.status === 'pending' || b.status === 'confirmed') && (
                          <Button size="small" color="error" variant="outlined" startIcon={<Cancel />} onClick={() => setCancelDialog(b.id)} sx={{ px: 1.5 }}>Cancel</Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 15 && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><Pagination count={Math.ceil(total/15)} page={page} onChange={(e,v)=>setPage(v)} color="primary" /></Box>}
        </>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Booking Workflow</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                {[
                  { key: 'auto_confirm', label: 'Auto-Confirm Bookings', desc: 'Skip landlord approval step' },
                  { key: 'require_payment_upfront', label: 'Require Payment Upfront', desc: 'Tenant must pay before confirmation' },
                  { key: 'allow_instant', label: 'Allow Instant Booking', desc: 'Enable one-click booking' },
                  { key: 'require_kyc', label: 'Require KYC for Booking', desc: 'Users must be verified to book' },
                ].map(item => (
                  <Stack key={item.key} direction="row" justifyContent="space-between" alignItems="center">
                    <Box><Typography fontWeight={600}>{item.label}</Typography><Typography variant="body2" color="text.secondary">{item.desc}</Typography></Box>
                    <Switch checked={bs[item.key]==='true'} onChange={(e)=>saveSetting(item.key, e.target.checked?'true':'false')} />
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Duration & Limits</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                {[
                  { key: 'min_duration_months', label: 'Min Duration (months)' },
                  { key: 'max_duration_months', label: 'Max Duration (months)' },
                  { key: 'max_pending', label: 'Max Pending Bookings per User' },
                  { key: 'expiry_hours', label: 'Pending Booking Expiry (hours)' },
                ].map(item => (
                  <Box key={item.key}><Typography fontWeight={600} gutterBottom>{item.label}</Typography>
                    <TextField size="small" type="number" value={bs[item.key]}
                      onChange={(e) => setBs(p=>({...p,[item.key]:e.target.value}))}
                      onKeyDown={integerOnly}
                      onBlur={(e) => saveSetting(item.key, e.target.value)} sx={{ width: 120 }} />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Platform Fee Configuration</Typography><Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Platform fees are applied to each booking. Only admins and landlords/agents can see the fee breakdown.</Typography>
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Enable Platform Fee</Typography><Typography variant="body2" color="text.secondary">Apply platform fee to all new bookings</Typography></Box>
                  <Switch checked={bs.platform_fee_enabled==='true'} onChange={(e)=>saveSetting('platform_fee_enabled', e.target.checked?'true':'false')} />
                </Stack>
                <Box><Typography fontWeight={600} gutterBottom>Fee Percentage (%)</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Percentage of booking amount charged as platform fee</Typography>
                  <TextField size="small" type="number" value={bs.platform_fee_percentage}
                    onChange={(e) => setBs(p=>({...p,platform_fee_percentage:e.target.value}))}
                    onKeyDown={decimalOnly}
                    onBlur={(e) => saveSetting('platform_fee_percentage', e.target.value)}
                    inputProps={{ min: 0, max: 50, step: 0.5 }} sx={{ width: 120 }} />
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Fee Mode</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>How the platform fee is calculated</Typography>
                  <Select size="small" value={bs.platform_fee_mode} onChange={(e) => { setBs(p=>({...p,platform_fee_mode:e.target.value})); saveSetting('platform_fee_mode', e.target.value); }} sx={{ minWidth: 200 }}>
                    <MenuItem value="per_booking">Per Booking (flat % per booking)</MenuItem>
                    <MenuItem value="per_person">Per Person (multiplied by occupants)</MenuItem>
                  </Select>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Fee Visibility</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {[
                  { role: 'Super Admin', visible: true, desc: 'Full fee breakdown and configuration' },
                  { role: 'Landlord / Agent', visible: true, desc: 'Can view fee deducted from their earnings' },
                  { role: 'Tenant', visible: false, desc: 'Platform fee is not visible to tenants' },
                ].map((item, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography fontWeight={600}>{item.role}</Typography>
                        <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                      </Box>
                      <Chip label={item.visible ? 'Visible' : 'Hidden'} size="small" color={item.visible ? 'success' : 'default'} />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Cancellation Policy</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Box><Typography fontWeight={600} gutterBottom>Cancellation Fee (%)</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Percentage of booking amount charged on cancellation</Typography>
                  <TextField size="small" type="number" value={bs.cancel_fee_percent}
                    onChange={(e) => setBs(p=>({...p,cancel_fee_percent:e.target.value}))}
                    onKeyDown={decimalOnly}
                    onBlur={(e) => saveSetting('cancel_fee_percent', e.target.value)} sx={{ width: 120 }} />
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Free Cancellation Window (hours)</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Time window for free cancellation after booking</Typography>
                  <TextField size="small" type="number" value={bs.cancel_window_hours}
                    onChange={(e) => setBs(p=>({...p,cancel_window_hours:e.target.value}))}
                    onKeyDown={integerOnly}
                    onBlur={(e) => saveSetting('cancel_window_hours', e.target.value)} sx={{ width: 120 }} />
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Cancellation Rules</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {[
                  { rule: 'Free cancellation within window', active: true },
                  { rule: 'Partial refund after window expires', active: true },
                  { rule: 'No refund within 24h of check-in', active: true },
                  { rule: 'Landlord can cancel with full refund', active: true },
                  { rule: 'Admin override for special cases', active: true },
                ].map((item, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CheckCircle color="success" sx={{ fontSize: 18 }} />
                        <Typography variant="body2">{item.rule}</Typography>
                      </Stack>
                      <Switch defaultChecked={item.active} />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Dialog open={!!cancelDialog} onClose={() => setCancelDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Booking</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Reason for cancellation..." sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog(null)}>Keep Booking</Button>
          <Button variant="contained" color="error" onClick={handleCancel} disabled={!cancelReason}>Cancel Booking</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminBookingsPage;
